import React from 'react';
import { Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface UnpivotNodeConfigProps {
    data: any;
    onChange: (key: string, value: any) => void;
    inputColumns: any[];
}

export const UnpivotNodeConfig: React.FC<UnpivotNodeConfigProps> = ({ data, onChange, inputColumns }) => {
    const unpivotCols = data.unpivot_columns || [];

    const handleColumnToggle = (colName: string, checked: boolean) => {
        const newCols = checked
            ? [...unpivotCols, colName]
            : unpivotCols.filter((c: any) => c !== colName);
        onChange('unpivot_columns', newCols);
    };

    return (
        <div className="space-y-6">
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 text-xs text-purple-800 flex gap-2">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                    <strong>Unpivot Operation:</strong> Rotates columns into rows.
                    <ul className="list-disc ml-4 mt-1 space-y-1">
                        <li>Selected <b>Columns</b> will be stacked into a single Value column.</li>
                        <li>Their headers will populate the Attribute column.</li>
                        <li>All other columns will be repeated for each row.</li>
                    </ul>
                </div>
            </div>

            {/* Unpivot Columns Selection */}
            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Columns to Unpivot (Rotate)</Label>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                    {inputColumns.length === 0 ? (
                        <div className="text-xs text-gray-400 italic p-2">No input columns available.</div>
                    ) : (
                        inputColumns.map((col) => {
                            const isChecked = unpivotCols.includes(col.name);
                            return (
                                <div key={col.name} className="flex items-center space-x-2 hover:bg-gray-100 p-1 rounded">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        checked={isChecked}
                                        onChange={(e) => handleColumnToggle(col.name, e.target.checked)}
                                    />
                                    <span className="text-[11px] text-gray-700 truncate cursor-default flex-1" title={col.name}>{col.name}</span>
                                    <span className="text-[9px] text-gray-400 uppercase">({col.type})</span>
                                </div>
                            );
                        })
                    )}
                </div>
                <p className="text-[10px] text-gray-400">These columns will disappear and become rows.</p>
            </div>

            {/* Output Column Names */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-700">Attribute Column Name</Label>
                    <Input
                        className="h-8 text-xs font-mono"
                        placeholder="e.g. Metric_Name"
                        value={data.var_name || 'Attribute'}
                        onChange={(e) => onChange('var_name', e.target.value)}
                    />
                    <p className="text-[10px] text-gray-400">Holds the header names.</p>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-700">Value Column Name</Label>
                    <Input
                        className="h-8 text-xs font-mono"
                        placeholder="e.g. Value"
                        value={data.value_name || 'Value'}
                        onChange={(e) => onChange('value_name', e.target.value)}
                    />
                    <p className="text-[10px] text-gray-400">Holds the cell values.</p>
                </div>
            </div>
        </div>
    );
};
